import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip, Dropdown, Badge } from "antd";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import classnames from "classnames";
import { ItemType } from "antd/lib/menu/interface";

export interface DropdownActionConfig {
  onClick: (id?: string | number) => void;
  icon?: IconProp;
  label: string;
  key?: string;
  testId?: string;
  disabled?: boolean;
  message?: string | JSX.Element;
}

export interface PrimaryActionsConfig extends DropdownActionConfig {
  customIcon?: React.ReactNode;
  withTooltip?: boolean;
  badgeCount?: number;
  isPrimary?: boolean;
  onDrop?: (e: React.DragEvent) => void;
}

export interface GroupedDropdownActionConfig {
  [groupTitle: string]: DropdownActionConfig[];
}

export interface DetailListActionsProps {
  primaryActions: PrimaryActionsConfig[];
  dropdownActions?: DropdownActionConfig[];
  groupedDropdownActions?: GroupedDropdownActionConfig;
  dropdownIcon?: IconProp;
  dropdownLabel?: string;
  dropdownTestId?: string;
  /* whether render dropdown before primary action buttons */
  dropdownFirst?: boolean;
  allowMessagesOnDisabled?: boolean;
}

const KEY_DIVIDER = "/-/";

/**
 * Used to render list of actions is Detail page like in email details page, question details page and so on
 */
export const DetailListActions = withTranslation(I18N_NS.PROJECT)(
  ({
    primaryActions,
    dropdownActions = [],
    groupedDropdownActions,
    t,
    dropdownIcon,
    dropdownLabel,
    dropdownTestId,
    dropdownFirst,
    allowMessagesOnDisabled,
  }: DetailListActionsProps & WithTranslation) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let handleClick: (param: any) => void;
    let menuItems: ItemType[] = [];
    if (groupedDropdownActions) {
      handleClick = ({ key }) => {
        const keys = key.split(KEY_DIVIDER);
        if (
          keys.length === 2 &&
          groupedDropdownActions[keys[0]][keys[1]] &&
          typeof groupedDropdownActions[keys[0]][keys[1]].onClick === "function"
        ) {
          // catch random exception
          groupedDropdownActions[keys[0]][keys[1]].onClick();
        }
      };
      menuItems = Object.entries(groupedDropdownActions).map(([label, items], groupIdx) => {
        return {
          type: "group",
          label: label,
          key: groupIdx.toString(),
          children: items.map<ItemType>((item, itemIdx) => ({
            key: `${label}${KEY_DIVIDER}${itemIdx}`,
            disabled: item.disabled,
            label: (
              <Tooltip title={allowMessagesOnDisabled || !item.disabled ? item.message : undefined} placement={"right"}>
                <div>
                  {item.icon && <FontAwesomeIcon fixedWidth={true} icon={item.icon} />}
                  {item.label}
                </div>
              </Tooltip>
            ),
          })),
        };
      });
    } else {
      handleClick = ({ key }) => {
        dropdownActions[key].onClick();
      };
      menuItems = dropdownActions.map((item, idx) => ({
        key: idx,
        disabled: item.disabled,
        label: (
          <Tooltip title={!item.disabled ? item.message : undefined} placement={"right"}>
            <div>
              {item.icon && <FontAwesomeIcon fixedWidth={true} icon={item.icon} />}
              {item.label}
            </div>
          </Tooltip>
        ),
      }));
    }
    const dropdownActionClassName = classnames("detail-action", { "first-action": dropdownFirst });
    const primaryActionClassName = classnames("detail-action", { "last-action": dropdownFirst });
    return (
      <div className="detail-actions">
        {primaryActions.map((action) => {
          const dragAndDropProps: Pick<
            React.DOMAttributes<HTMLDivElement>,
            "onDragEnter" | "onDragLeave" | "onDrop" | "onDragOver"
          > = {};
          if (action.onDrop) {
            dragAndDropProps.onDragEnter = (e) => {
              e.dataTransfer.dropEffect = "move";

              e.currentTarget.classList.add("droppable");
            };
            dragAndDropProps.onDragLeave = (e) => {
              e.currentTarget.classList.remove("droppable");
            };
            dragAndDropProps.onDragOver = (e) => {
              e.preventDefault();
            };
            dragAndDropProps.onDrop = (e) => {
              e.currentTarget.classList.remove("droppable");
              return action.onDrop(e);
            };
          }

          return (
            <div
              className={classnames(primaryActionClassName, { primary: action.isPrimary, disabled: action.disabled })}
              key={action.label}
              data-testid={action.testId || action.label}
              onClick={() => (!action.disabled ? action.onClick() : undefined)}
              {...dragAndDropProps}
            >
              {action.withTooltip ? (
                <Tooltip title={action.label}>
                  <>
                    {action.customIcon ? (
                      action.customIcon
                    ) : (
                      <Badge count={action.badgeCount} className="detail-action-icon">
                        <FontAwesomeIcon icon={action.icon} />
                      </Badge>
                    )}
                  </>
                </Tooltip>
              ) : action.customIcon ? (
                action.customIcon
              ) : (
                <>
                  <Badge count={action.badgeCount} className="detail-action-icon">
                    <FontAwesomeIcon icon={action.icon} fixedWidth={true} />
                  </Badge>
                  {action.label}
                </>
              )}
            </div>
          );
        })}
        {dropdownActions.length || groupedDropdownActions ? (
          <Dropdown
            menu={{ items: menuItems, onClick: handleClick, className: "documents-actions" }}
            trigger={["click"]}
            styles={{ root: { zIndex: 99999999 } }}
          >
            <div
              className={dropdownActionClassName}
              data-testid={dropdownTestId || dropdownLabel || "detail-action-dropdown"}
            >
              <FontAwesomeIcon className="detail-action-icon" icon={dropdownIcon || ["far", "ellipsis-v"]} />
              {dropdownLabel || t("Select action")}
            </div>
          </Dropdown>
        ) : null}
      </div>
    );
  }
);
